/**
 * A prerender manifest's shape: one entry per route the build writes a
 * document for, where that document goes, and what its head says.
 *
 * Each app keeps its own manifest (src/prerender-manifest.ts) — which routes,
 * and why, is the app's decision — and types it with `PrerenderRoute` from
 * here, so ./pass.ts can check and write every app's routes the same way.
 */

/**
 * The attribute ./pass.ts stamps on the mount point with the path it
 * rendered, and ./mount.ts reads to decide between hydrating and mounting.
 * Here rather than in either so the build pass never imports react-dom/client
 * and the browser bundle never imports node:fs.
 */
export const PRERENDERED_PATH_ATTRIBUTE = "data-prerendered-path";

/** The 1.91:1 size the large-image card layouts crop to. */
export const SOCIAL_CARD_SIZE = { width: 1200, height: 630 } as const;

/** A social card image: a file in the app's public/, and what it shows. */
export interface SocialImage {
  /** Root-relative, as the asset host serves it: `/og/site.png`. */
  readonly path: string;
  readonly width: number;
  readonly height: number;
  /** `og:image:alt`: what the card shows, for a reader who cannot see it. */
  readonly alt: string;
}

/**
 * What every document one app writes shares in its head.
 *
 * `origin` is the production origin, on every target's build. A scraper and
 * a search engine need absolute URLs, and the copy of a page that should be
 * shared and indexed is the production one, so a staging document names
 * production as its canonical rather than itself.
 */
export interface SiteHead {
  /** `https://host`, no trailing slash. */
  readonly origin: string;
  /** `og:site_name`. */
  readonly siteName: string;
  /** The card a route without an `image` of its own gets. */
  readonly image: SocialImage;
}

/** An extra `<link>` in a route's head: `rel="alternate"`, `rel="site.standard.document"`. */
export interface HeadLink {
  readonly tag: "link";
  readonly rel: string;
  readonly href: string;
  readonly type?: string;
  readonly title?: string;
}

/** An extra `<meta>` in a route's head, keyed by `name` or `property`. */
export interface HeadMeta {
  readonly tag: "meta";
  readonly attribute: "name" | "property";
  readonly key: string;
  readonly content: string;
}

export type HeadTag = HeadLink | HeadMeta;

/** One prerendered route: where it lives, and what its head says. */
export interface PrerenderRoute {
  /** The path the client router matches, exactly as a visitor types it. */
  readonly path: string;
  /** Where the document is written, relative to `dist/`. Use `outputFileFor`. */
  readonly outputFile: string;
  /** `<title>`, and the card's `og:title`. */
  readonly title: string;
  /** `<meta name="description">`, and the card's `og:description`. */
  readonly description: string;
  /** This route's card, where the site's default would be wrong. */
  readonly image?: SocialImage;
  /**
   * This is the document the host serves for every path no other route wrote
   * (`not_found_handling: "404-page"` serves `404.html`). Its head names no
   * URL — a canonical or `og:url` would tell every crawler and scraper that
   * each mistyped link is this one page — and carries `robots: noindex`,
   * because the asset worker also serves it at its own path with a 200.
   */
  readonly notFound?: true;
  /**
   * Tags this route adds to the head the pass writes, after the card. A meta
   * whose key the pass already writes replaces that tag in place (`og:type`
   * → `article`), except the URL and image tags the card checks govern,
   * which `headFor` refuses to have replaced.
   */
  readonly headTags?: readonly HeadTag[];
  /**
   * A phrase from the route's own prose that MUST appear in the rendered
   * markup, asserted by `runPrerenderPass` (./pass.ts).
   *
   * The emptiness check beside it proves a document came out. That is not the
   * claim a manifest makes — the claim is that the RIGHT document came out.
   * The two come apart when a component throws at build time and an
   * ErrorBoundary renders its fallback: the markup is non-empty, the
   * emptiness check passes, and the route ships an apology where its pitch
   * should be.
   *
   * Pick something the page cannot render without — a sentence from the copy,
   * not a class name or a heading a redesign would reword. It is checked
   * against the body markup only, before the `<title>` and description
   * `<meta>` are written, so overlapping either is harmless; it must survive
   * HTML escaping, so prefer a phrase with no apostrophes, ampersands, or
   * quotes.
   */
  readonly mustContain: string;
}

/**
 * Where a route's document is written.
 *
 * `/foo` becomes `foo.html`, NOT `foo/index.html`, and the difference is a
 * round trip. Cloudflare's asset worker resolves an extensionless path under
 * `html_handling: "auto-trailing-slash"` in this order — read out of the
 * implementation wrangler 4.123 ships, in
 * miniflare/dist/src/workers/assets/assets.worker.js, not out of the docs:
 *
 *   1. the exact path as a file        → serve 200
 *   2. `<path>.html`                   → serve 200
 *   3. `<path>/index.html`             → **301 to `<path>/`**
 *
 * So a directory index would answer every cold arrival with a redirect before
 * the document. The flat form is served directly at the URL links point at.
 *
 * `/` is the one path with no name of its own: it is `index.html`. Whether
 * that file has a second job (apps/retells' SPA fallback) is the app's
 * business, and its manifest says so.
 */
export function outputFileFor(path: string): string {
  if (path === "/") return "index.html";
  return `${path.replace(/^\//, "")}.html`;
}

/** The title `routes` gives `path`, or `fallback` for a path it does not name. */
export function titleFor(
  routes: readonly PrerenderRoute[],
  path: string,
  fallback: string,
): string {
  return routes.find((route) => route.path === path)?.title ?? fallback;
}
